import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Search, RefreshCw, CheckCircle, XCircle, Trophy, Globe } from 'lucide-react';
import { api } from '../../context/AuthContext';

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  return typeof d === 'string' ? d : err.message || 'Request failed';
};

function PositionBadge({ position }) {
  if (!position) return <span className="text-xs text-slate-400">Not ranked</span>;
  const color = position <= 3 ? 'text-emerald-600 dark:text-emerald-400' : position <= 10 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400';
  return <span className={`text-sm font-bold ${color}`}>#{position}</span>;
}

export default function SerpRankingPage() {
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Single check
  const [keyword, setKeyword] = useState('');
  const [domain, setDomain] = useState('');
  const [country, setCountry] = useState('US');
  const [singleResult, setSingleResult] = useState(null);

  // Batch check
  const [batchKeywords, setBatchKeywords] = useState('');
  const [batchDomain, setBatchDomain] = useState('');
  const [batchResults, setBatchResults] = useState([]);

  // Update all
  const [updateDomain, setUpdateDomain] = useState('');
  const [updateResult, setUpdateResult] = useState(null);

  const handleSingle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSingleResult(null);
    try {
      const { data } = await api.post('/serp-ranking/check', { keyword, target_domain: domain, country });
      if (data.success) setSingleResult(data.result);
    } catch (err) {
      setError(errDetail(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBatch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setBatchResults([]);
    try {
      const keywords = batchKeywords.split('\n').map((k) => k.trim()).filter(Boolean);
      const { data } = await api.post('/serp-ranking/check-batch', {
        keywords,
        target_domain: batchDomain,
        country,
      });
      if (data.success) setBatchResults(data.results || []);
    } catch (err) {
      setError(errDetail(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAll = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUpdateResult(null);
    try {
      const { data } = await api.post('/serp-ranking/update-all', { target_domain: updateDomain, country });
      if (data.success) setUpdateResult(data);
    } catch (err) {
      setError(errDetail(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Baseline Ranking Check</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Check real Google SERP positions for any domain and keyword — goes beyond GSC impressions.
          </p>
        </div>
      </div>

      {/* Country selector */}
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-slate-400" />
        <label className="text-xs text-slate-600 dark:text-slate-400">Country</label>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1"
        >
          {['US', 'GB', 'CA', 'AU', 'IN', 'DE', 'FR', 'NP'].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'single', label: 'Single Keyword' },
          { id: 'batch', label: 'Batch Check' },
          { id: 'update', label: 'Update All Tracked' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Single */}
      {activeTab === 'single' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
            <form onSubmit={handleSingle} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Keyword *</label>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="seo tools"
                    required
                    className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Target Domain *</label>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="yoursite.com"
                    required
                    className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                {loading ? 'Checking SERP...' : 'Check Position'}
              </button>
            </form>
          </div>

          {singleResult && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">"{singleResult.keyword}"</p>
                  <p className="text-sm text-slate-500">{singleResult.target_domain}</p>
                </div>
                <div className="text-center">
                  {singleResult.found ? (
                    <>
                      <p className="text-4xl font-black text-blue-600 dark:text-blue-400">#{singleResult.position}</p>
                      <p className="text-xs text-emerald-600 flex items-center gap-1 justify-center"><CheckCircle className="w-3 h-3" /> Found in SERP</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-black text-slate-400">N/A</p>
                      <p className="text-xs text-red-500 flex items-center gap-1 justify-center"><XCircle className="w-3 h-3" /> Not ranked</p>
                    </>
                  )}
                </div>
              </div>

              {singleResult.url && (
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 text-sm">
                  <p className="font-medium text-slate-900 dark:text-white truncate">{singleResult.title}</p>
                  <a href={singleResult.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block">{singleResult.url}</a>
                  {singleResult.snippet && <p className="text-xs text-slate-500 mt-1">{singleResult.snippet}</p>}
                </div>
              )}

              {singleResult.top_3?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Top 3 Results</p>
                  <div className="space-y-1.5">
                    {singleResult.top_3.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-900">
                        <span className="text-sm font-bold text-slate-400 w-5 shrink-0">#{r.position}</span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-900 dark:text-white truncate">{r.title}</p>
                          <p className="text-[10px] text-slate-400 truncate">{r.domain}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {singleResult.serp_features && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(singleResult.serp_features).filter(([, v]) => v).map(([k]) => (
                    <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {k.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Batch */}
      {activeTab === 'batch' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
            <form onSubmit={handleBatch} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Target Domain *</label>
                <input
                  type="text"
                  value={batchDomain}
                  onChange={(e) => setBatchDomain(e.target.value)}
                  placeholder="yoursite.com"
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Keywords (one per line) *</label>
                <textarea
                  value={batchKeywords}
                  onChange={(e) => setBatchKeywords(e.target.value)}
                  placeholder={"seo tools\nbest seo software\nkeyword research tool"}
                  rows={6}
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y font-mono"
                />
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                {loading ? 'Checking...' : 'Run Batch Check'}
              </button>
            </form>
          </div>

          {batchResults.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-600 dark:text-slate-400 px-4 py-2">Keyword</th>
                    <th className="text-center text-xs font-medium text-slate-600 dark:text-slate-400 px-4 py-2">Position</th>
                    <th className="text-left text-xs font-medium text-slate-600 dark:text-slate-400 px-4 py-2">Ranking URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {batchResults.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-slate-900 dark:text-white">{r.keyword}</td>
                      <td className="px-4 py-2 text-center"><PositionBadge position={r.position} /></td>
                      <td className="px-4 py-2">
                        {r.url ? (
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block max-w-xs">{r.url}</a>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Update All */}
      {activeTab === 'update' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Updates real SERP positions for all tracked keywords in the Keywords module and appends to their position history.
            </p>
            <form onSubmit={handleUpdateAll} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Target Domain *</label>
                <input
                  type="text"
                  value={updateDomain}
                  onChange={(e) => setUpdateDomain(e.target.value)}
                  placeholder="yoursite.com"
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm max-w-sm"
                />
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Updating...' : 'Update All Keyword Positions'}
              </button>
            </form>
          </div>

          {updateResult && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
              <p className="font-medium text-slate-900 dark:text-white mb-3">{updateResult.message}</p>
              <div className="space-y-1.5">
                {(updateResult.results || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    {r.found ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                    <span className="text-slate-700 dark:text-slate-300 flex-1">{r.keyword}</span>
                    <PositionBadge position={r.position} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
